import { provider as IProvider } from 'web3-core';
import EventTypes from './constants/eventTypes';
import { callbackEventProvider, stateEventObserver } from './services/EventObserver';
import { treasury } from './stateManagement/Treasury';
import {
  toggleModalType, toggleLedgerModalType, setNetworkType, INetwork,
} from './stateManagement/stateConstants';
import {
  LEDGER_LS_KEY, TWalletType, WALLET_TYPES, INPAGE_KEY, TREZOR_LS_KEY,
} from './constants/wallet';
import { getSupportedWallets } from './services/providerService';
import themes from './styles/colors';
import './services/gtm';
import './styles/normalize.css';

const derivationPaths = Symbol('derivationPaths');
const walletProviderType = Symbol('providerType');
const walletConnector = Symbol('walletConnector');

interface IConfig {
  portisDAppId?: string
  fortmaticKey?: string
  darkMode?: boolean
  container?: string
}

export interface IConnect extends INetwork {
  connectionType?: TWalletType
  onConnectStart?: () => void
  savePath?: boolean
  trezorPath?: string
  removeLSKey?: boolean,
}

interface IResponse {
  provider: string
  connectionType: TWalletType
  connector: string,
  account: string,
}

interface IConnectionResponse {
  provider: IProvider,
  account: string,
  connectionType: TWalletType,
}

interface IHDDerivationPaths {
  ledger?: string,
  trezor?: string,
}

export class Web3WalletConnector {
  mounted: boolean;

  [walletProviderType]: null | string;

  [walletConnector]: null | string;

  [derivationPaths]: IHDDerivationPaths;

  constructor({ portisDAppId, fortmaticKey }: IConfig = {}) {
    treasury.fortmatic = fortmaticKey;
    treasury.portis = portisDAppId;
    this[derivationPaths] = {};
    this.setTheme();
    callbackEventProvider.on({
      eventType: EventTypes.MOUNTED,
      callback: (mounted) => {
        console.log('WalletConnector mounted:', mounted);
        this.mounted = mounted;
      },
    });
    callbackEventProvider.on({
      eventType: EventTypes.CONNECT_SUCCESS_MODULE,
      callback: ({
        connectionType, connector, ledgerPath, trezorPath,
      }) => {
        this[walletProviderType] = connectionType;
        this[walletConnector] = connector;
        this[derivationPaths].ledger = ledgerPath;
        this[derivationPaths].trezor = trezorPath;
      },
    });
  }

  get connectionType() {
    return this[walletProviderType];
  }

  set connectionType(newType) {
    throw new Error('Connection type can be set only from internal instance!');
  }

  get connector() {
    return this[walletConnector];
  }

  set connector(newConnector) {
    throw new Error('Connector can be set only from internal instance!');
  }

  setTheme() {
    Object.entries(themes).forEach(([key, value]) => {
      document.documentElement.style.setProperty(key, value);
    });
  }

  openModal(network: INetwork) {
    this[walletConnector] = null;
    this[walletProviderType] = null;
    if (network) {
      stateEventObserver.emit(EventTypes.DISPATCH, setNetworkType.emit(network));
    }
    stateEventObserver.emit(EventTypes.DISPATCH, toggleModalType.emit(true));
  }

  closeModal() {
    stateEventObserver.emit(EventTypes.DISPATCH, toggleModalType.emit(false));
  }

  getSupportedWallets() {
    return getSupportedWallets();
  }

  connect({
    connectionType, rpcUrl, networkId, ledgerPath, trezorPath, ...rest
  }: IConnect = {}) {
    if (!this.mounted) {
      throw new Error('Component not mounted!');
    }
    return new Promise<IConnectionResponse>((resolve, reject) => {
      const handleEvent = (res: IResponse) => {
        const {
          provider, connectionType: providerConnectionType, connector, account, ...additions
        } = res;
        this[walletProviderType] = providerConnectionType;
        this[walletConnector] = connector;
        /* eslint-disable @typescript-eslint/no-use-before-define */
        callbackEventProvider.off({ eventType: EventTypes.CONNECT_ERROR_COMPONENT, callback: handleError });
        /* eslint-enable @typescript-eslint/no-use-before-define */
        callbackEventProvider.off({ eventType: EventTypes.CONNECT_SUCCESS_COMPONENT, callback: handleEvent });
        resolve({
          provider, account, connectionType: providerConnectionType, ...additions,
        });
      };

      const handleError = (err: Error) => {
        callbackEventProvider.off({ eventType: EventTypes.CONNECT_ERROR_COMPONENT, callback: handleError });
        callbackEventProvider.off({ eventType: EventTypes.CONNECT_SUCCESS_COMPONENT, callback: handleEvent });
        reject(err);
      };

      callbackEventProvider.on({ eventType: EventTypes.CONNECT_SUCCESS_COMPONENT, callback: handleEvent });
      callbackEventProvider.on({ eventType: EventTypes.CONNECT_ERROR_COMPONENT, callback: handleError });

      if (connectionType) {
        const ledgerLastPath = localStorage.getItem(LEDGER_LS_KEY);
        const terzorLastPath = localStorage.getItem(TREZOR_LS_KEY);
        const options: IConnect = {
          connectionType,
          rpcUrl,
          networkId,
          ledgerPath: ledgerPath || ledgerLastPath,
          trezorPath: trezorPath || terzorLastPath,
          ...rest,
        };
        if (connectionType === WALLET_TYPES.ledger && !options.ledgerPath) {
          this.openModal({ rpcUrl, networkId });
          stateEventObserver.emit(EventTypes.DISPATCH, toggleLedgerModalType.emit(true));
        } if (connectionType === WALLET_TYPES.trezor && !options.trezorPath) {
          this.openModal({ rpcUrl, networkId });
          stateEventObserver.emit(EventTypes.DISPATCH, toggleLedgerModalType.emit(true));
        } else {
          stateEventObserver.emit(EventTypes.CONNECT_REQUEST_COMPONENT, options);
        }
      } else {
        this.openModal({ rpcUrl, networkId });
      }
    });
  }

  disconnect() {
    stateEventObserver.emit(EventTypes.DISCONNECT, null);
  }
}

export const inPageKey = INPAGE_KEY;
export { App as WalletConnector } from './components/App';
export type { App as WalletConnectorProps } from './components/App';
export { networks } from './constants/networks';